import React, { useEffect, useState } from "react";

import {
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Title,
  Text,
  Tabs,
  Box,
  Badge,
} from "@mantine/core";
import SfcLicenceReadOnlyList from "./sfcLicenceReadOnlyList";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import DataTable from "./dataTable";
import FormSection from "./formSection";
import { getLicenceRoleLabel, getMpfaRoleLabel, getSfcRoleLabel } from "../data/licenceRoles";

const CPDMetric = ({ earned, need, label }) => (
  <Box>
    <Group align="baseline" spacing={4}>
      <Title color={earned >= (need ?? 0) ? "cyan" : "red"} order={2}>
        {earned ?? 0}
      </Title>
      <Text color="dimmed" size="sm">
        / {need ?? 0}
      </Text>
    </Group>
    <Text color="dimmed" size="xs" mt={4}>
      {label}
    </Text>
  </Box>
);

const CPDCard = ({
  label,
  totalCreditNeed,
  totalCreditEarn,
  coreEarn,
  coreNeed,
  licence,
  coreLabel = "Core",
  optionalEarn,
  optionalNeed,
  optionalLabel,
  secondOptionalEarn,
  secondOptionalNeed,
  secondOptionalLabel,
  ...props
}) => {
  const metricCount =
    2 +
    (optionalLabel ? 1 : 0) +
    (secondOptionalLabel ? 1 : 0);

  return (
    <Paper radius="md" p="lg" key={label} {...props} shadow="md">
      <Group position="apart" mb="xl">
        <Title order={6}> {label} </Title>
        <Text size={"xs"} color="dimmed">
          {licence}
        </Text>
      </Group>
      <SimpleGrid
        cols={metricCount}
        spacing="xl"
        breakpoints={[{ maxWidth: "sm", cols: metricCount > 2 ? 2 : 1 }]}
      >
        <CPDMetric
          earned={totalCreditEarn}
          need={totalCreditNeed}
          label="Total Credit"
        />
        <CPDMetric earned={coreEarn} need={coreNeed} label={coreLabel} />
        {optionalLabel && (
          <CPDMetric
            earned={optionalEarn}
            need={optionalNeed}
            label={optionalLabel}
          />
        )}
        {secondOptionalLabel && (
          <CPDMetric
            earned={secondOptionalEarn}
            need={secondOptionalNeed}
            label={secondOptionalLabel}
          />
        )}
      </SimpleGrid>
    </Paper>
  );
};

const SFCMetricCard = ({ label, earned, need, ...props }) => {
  const needDisplay = Math.max(0, need ?? 0);
  return (
    <Paper radius="md" p="md" shadow="md" {...props}>
      <Text size="xs" color="dimmed" mb="xs">
        {label}
      </Text>
      <Group align="baseline" spacing={4}>
        <Title color={earned >= needDisplay ? "cyan" : "red"} order={3}>
          {earned}
        </Title>
        <Text color="dimmed" size="sm">
          / {needDisplay}
        </Text>
      </Group>
    </Paper>
  );
};

const CPDMPFStats = ({ cpdData }) => {
  return (
    cpdData && (
      <Box mt="xl">
        <CPDCard
          label="MPF"
          licence={cpdData.mpfaNumber}
          totalCreditEarn={cpdData.MPFCoreCredit + cpdData.MPFNonCoreCredit}
          totalCreditNeed={cpdData.mpf_core_need + cpdData.mpf_noncore_need}
          coreEarn={cpdData.MPFCoreCredit}
          coreNeed={cpdData.mpf_core_need}
          coreLabel="Core"
        />
      </Box>
    )
  );
};

const CPDIAStats = ({ cpdData }) => {
  if (!cpdData) return null;

  const iaErEarned =
    cpdData.IAERCreditEffective ??
    (cpdData.IAERCredit ?? 0) + (cpdData.IAROCredit ?? 0);
  const showIlas = (cpdData.ia_ilas_need ?? 0) > 0;
  const showIaRo = (cpdData.ia_ro_need ?? 0) > 0;

  return (
    <Box mt="xl">
        <CPDCard
          label="IA"
          licence={cpdData.iaNumber}
          totalCreditEarn={
            (cpdData.IACredit ?? 0) +
            (cpdData.IAERCredit ?? 0) +
            (cpdData.IAROCredit ?? 0) +
            (cpdData.IAILASCredit ?? 0)
          }
          totalCreditNeed={
            (cpdData.ia_need ?? 0) +
            (cpdData.iaer_need ?? 0) +
            (cpdData.ia_ro_need ?? 0) +
            (cpdData.ia_ilas_need ?? 0)
          }
          coreEarn={iaErEarned}
          coreNeed={cpdData.iaer_need}
          coreLabel="Ethics"
          optionalEarn={showIlas ? cpdData.IAILASCredit ?? 0 : undefined}
          optionalNeed={showIlas ? cpdData.ia_ilas_need : undefined}
          optionalLabel={showIlas ? "ILAS" : undefined}
          secondOptionalEarn={showIaRo ? cpdData.IAROCredit ?? 0 : undefined}
          secondOptionalNeed={showIaRo ? cpdData.ia_ro_need : undefined}
          secondOptionalLabel={showIaRo ? "IA RO" : undefined}
        />
    </Box>
  );
};

const IaInformationReadOnly = ({ user }) => {
  const { t } = useTranslation();
  const ci = user?.consultantInfo;
  if (!ci?.iaNumber?.trim?.() && !ci?.iaNumber) return null;

  const fmt = (d) => (d ? moment(d).format("YYYY-MM-DD") : "—");

  return (
    <FormSection title="IA" mt="xl">
      <Stack spacing="xs">
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("IA Number")}:{" "}
          </Text>
          {ci.iaNumber || "—"}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("IA Role")}:{" "}
          </Text>
          {getLicenceRoleLabel(ci.iaRole, t)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("First Registration Date")}:{" "}
          </Text>
          {fmt(ci.iaFirstRegisterDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Start Date")}:{" "}
          </Text>
          {fmt(ci.iaApplyDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("End Date")}:{" "}
          </Text>
          {fmt(ci.iaEffectiveDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Termination Date")}:{" "}
          </Text>
          {fmt(ci.iaTerminationDate)}
        </Text>
      </Stack>
    </FormSection>
  );
};

const MpfaInformationReadOnly = ({ user }) => {
  const { t } = useTranslation();
  const ci = user?.consultantInfo;
  if (!ci?.mpfaNumber?.trim?.() && !ci?.mpfaNumber) return null;

  const fmt = (d) => (d ? moment(d).format("YYYY-MM-DD") : "—");

  return (
    <FormSection title="MPFA" mt="xl">
      <Stack spacing="xs">
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("MPFA Number")}:{" "}
          </Text>
          {ci.mpfaNumber || "—"}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("First Registration Date")}:{" "}
          </Text>
          {fmt(ci.mpfaFirstRegisterDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("MPFA Role")}:{" "}
          </Text>
          {getMpfaRoleLabel(ci.mpfaRole, t)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Effective Date")}:{" "}
          </Text>
          {fmt(ci.mpfaEffectiveDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Termination Date")}:{" "}
          </Text>
          {fmt(ci.mpfaTerminationDate)}
        </Text>
      </Stack>
    </FormSection>
  );
};

const SfcInformationReadOnly = ({ user }) => {
  const { t } = useTranslation();
  const ci = user?.consultantInfo;
  if (!ci?.sfcNumber?.trim?.() && !ci?.sfcNumber) return null;

  const fmt = (d) => (d ? moment(d).format("YYYY-MM-DD") : "—");

  return (
    <FormSection title="SFC" mt="xl">
      <Stack spacing="xs">
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("SFC Licence Number")}:{" "}
          </Text>
          {ci.sfcNumber || "—"}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("SFC Role")}:{" "}
          </Text>
          {getSfcRoleLabel(ci.sfcRole, t)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Effective Date")}:{" "}
          </Text>
          {fmt(ci.sfcEffectiveDate)}
        </Text>
        <Text size="sm">
          <Text span color="dimmed" size="xs">
            {t("Termination Date")}:{" "}
          </Text>
          {fmt(ci.sfcTerminationDate)}
        </Text>
      </Stack>
    </FormSection>
  );
};

const CPDSFCStats = ({ cpdData }) => {
  const { t } = useTranslation();
  if (!cpdData) return null;

  if (cpdData.sfcEligible === false) {
    return (
      <Text size="sm" color="dimmed" mt="xl">
        {t("No SFC licence configured for this user.")}
      </Text>
    );
  }

  const totalEarned =
    (cpdData.SFCCredit ?? 0) + (cpdData.SFCECCredit ?? 0);
  const roleLabel = getSfcRoleLabel(cpdData.sfcRole, t);

  return (
    <>
      <Text size="xs" color="dimmed" mt="md">
        {roleLabel}
      </Text>
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mt="md">
        <SFCMetricCard
          label={t("Total CPT")}
          earned={totalEarned}
          need={cpdData.sfc_total_need}
        />
        <SFCMetricCard
          label={t("SFC EC")}
          earned={cpdData.SFCECCredit ?? 0}
          need={cpdData.sfc_ec_need}
        />
      </SimpleGrid>
      <Text size="xs" color="dimmed" mt="xs">
        {cpdData.sfcNumber}
      </Text>
    </>
  );
};

const BASE_CPD_COLUMNS = [
  {
    field: "dateEnroll",
    sortable: true,
    headerName: "Course Date",
    cellRender: (col, data) => moment(data.dateEnroll).format("LL"),
  },
  {
    field: "courseTitle",
    headerName: "Course",
  },
  {
    field: "courseCode",
    headerName: "Course Code",
  },
  {
    field: "passApproval",
    headerName: "Status",
    cellRender: (col, data) =>
      data.passApproval === false ? (
        <Badge color="orange" variant="light">
          PENDING APPROVAL
        </Badge>
      ) : (
        <Badge color="green" variant="light">
          APPROVED
        </Badge>
      ),
  },
];

const CPD_RECORD_COLUMNS = {
  IA: [
    ...BASE_CPD_COLUMNS,
    { field: "IACredit", headerName: "IA" },
    { field: "IAERCredit", headerName: "IAER" },
    { field: "IAROCredit", headerName: "IA RO" },
    { field: "IAILASCredit", headerName: "ILAS" },
  ],
  MPF: [
    ...BASE_CPD_COLUMNS,
    { field: "MPFCoreCredit", headerName: "MPF (Core)" },
    { field: "MPFNonCoreCredit", headerName: "MPF (Non-Core)" },
  ],
};

const CPDRecords = ({ records, creditType = "IA" }) => {
  const { t } = useTranslation();
  const columns =
    creditType === "SFC"
      ? [
          ...BASE_CPD_COLUMNS,
          { field: "SFCCredit", headerName: t("SFC") },
          { field: "SFCECCredit", headerName: t("SFC EC") },
        ]
      : CPD_RECORD_COLUMNS[creditType] ?? CPD_RECORD_COLUMNS.IA;

  const sectionTitle =
    creditType === "SFC" ? t("CPT COURSES") : t("CPD COURSES");

  return (
    records && (
      <FormSection title={sectionTitle} mt="xl">
        <DataTable
          hideSearchBar={true}
          data={records}
          hidePagination={true}
          columns={columns}
        />
      </FormSection>
    )
  );
};

const CPDIA = ({ user, refreshKey = 0 }) => {
  const [years, setYears] = useState([]);
  const [cpdData, setCpdData] = useState();
  const numYears = 3;
  const [api] = useServerApi();

  const currentYear = moment().year();
  const startYear = moment().isBefore(`${currentYear}-08-01`)
    ? currentYear
    : currentYear + 1;

  useEffect(() => {
    const yrs = [];
    for (let i = 0; i < numYears; i++) {
      let yr = startYear - i;
      yrs.push({
        label: `${yr - 1} - ${yr}`,
        value: yr,
      });
    }
    setYears(yrs);
    fetchCPD(startYear);
  }, []);

  useEffect(() => {
    fetchCPD(startYear);
  }, [user._id, refreshKey]);

  const fetchCPD = async (year) => {
    try {
      let result = await api.Cpd.getUserDetail({
        year,
        userId: user._id,
        type: "IA",
      });
      if (!result || !result.data) return;
      setCpdData(result.data);
    } catch (error) {
      console.log(error);
    }
  };

  const { t } = useTranslation();
  return (
    <>
      <Group>
        <Select
          data={years}
          with={10}
          defaultValue={startYear}
          onChange={(yr) => fetchCPD(yr)}
        />
      </Group>
      <CPDIAStats cpdData={cpdData} />
      <CPDRecords records={cpdData?.cpdRecords} creditType="IA" />
      <Group>
        <Text size={"xs"} color="dimmed">
          {t("Period")} : {cpdData?.IAStartDate} to {cpdData?.IADueDate}
        </Text>
      </Group>
      <IaInformationReadOnly user={user} />
    </>
  );
};

const CPDMPF = ({ user, refreshKey = 0 }) => {
  const [years, setYears] = useState([]);
  const [cpdData, setCpdData] = useState();
  const numYears = 3;
  const [api] = useServerApi();
  const startYear = moment().year();

  useEffect(() => {
    const yrs = [];
    for (let i = 0; i < numYears; i++) {
      let yr = startYear - i;
      yrs.push({
        label: yr,
        value: yr,
      });
    }
    setYears(yrs);
  }, []);

  useEffect(() => {
    fetchCPD(startYear);
  }, [user._id, refreshKey]);

  const fetchCPD = async (year) => {
    try {
      let result = await api.Cpd.getUserDetail({
        year,
        userId: user._id,
        type: "MPF",
      });
      if (!result || !result.data) return;
      setCpdData(result.data);
    } catch (error) {
      console.log(error);
    }
  };

  const { t } = useTranslation();
  return (
    <>
      <Group>
        <Select
          data={years}
          with={10}
          defaultValue={moment().year()}
          onChange={fetchCPD}
        />
      </Group>
      <CPDMPFStats cpdData={cpdData} />
      <CPDRecords records={cpdData?.cpdRecords} creditType="MPF" />
      <Group>
        <Text size={"xs"} color="dimmed">
          {t("Period")} : {cpdData?.MPFStartDate} to {cpdData?.MPFDueDate}
        </Text>
      </Group>
      <MpfaInformationReadOnly user={user} />
    </>
  );
};

const CPDSFC = ({ user, showSfcLicences = true, refreshKey = 0 }) => {
  const [years, setYears] = useState([]);
  const [cpdData, setCpdData] = useState();
  const numYears = 3;
  const [api] = useServerApi();
  const startYear = moment().year();
  const { t } = useTranslation();

  useEffect(() => {
    const yrs = [];
    for (let i = 0; i < numYears; i++) {
      let yr = startYear - i;
      yrs.push({
        label: `${yr}`,
        value: yr,
      });
    }
    setYears(yrs);
  }, []);

  useEffect(() => {
    fetchCPD(startYear);
  }, [user._id, refreshKey]);

  const fetchCPD = async (year) => {
    try {
      let result = await api.Cpd.getUserDetail({
        year,
        userId: user._id,
        type: "SFC",
      });
      if (!result || !result.data) return;
      setCpdData(result.data);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Group>
        <Select
          data={years}
          with={10}
          defaultValue={startYear}
          onChange={fetchCPD}
        />
      </Group>
      {showSfcLicences && (
        <SfcLicenceReadOnlyList
          sfcLicences={user?.consultantInfo?.sfcLicences}
          mt="xl"
          mb="md"
        />
      )}
      <CPDSFCStats cpdData={cpdData} />
      <CPDRecords records={cpdData?.cpdRecords} creditType="SFC" />
      <Group>
        <Text size={"xs"} color="dimmed">
          {t("Period")} : {cpdData?.SFCStartDate} to {cpdData?.SFCDueDate}
        </Text>
      </Group>
      <SfcInformationReadOnly user={user} />
    </>
  );
};

const UserCPDDetail = ({
  form: mainForm,
  mainUser,
  showSfcLicences = true,
  refreshKey = 0,
}) => {
  const user = mainUser ? mainUser : mainForm.values;
  const { t } = useTranslation();
  return (
    <Tabs defaultValue="IA" variant="outline">
      <Tabs.List>
        <Tabs.Tab value="IA">IA</Tabs.Tab>
        <Tabs.Tab value="MPF">MPF</Tabs.Tab>
        <Tabs.Tab value="SFC">{t("SFC")}</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="IA" pt="xl">
        <CPDIA user={user} refreshKey={refreshKey} />
      </Tabs.Panel>

      <Tabs.Panel value="MPF" pt="xl">
        <CPDMPF user={user} refreshKey={refreshKey} />
      </Tabs.Panel>

      <Tabs.Panel value="SFC" pt="xl">
        <CPDSFC
          user={user}
          showSfcLicences={showSfcLicences}
          refreshKey={refreshKey}
        />
      </Tabs.Panel>
    </Tabs>
  );
};

export default UserCPDDetail;
